import { db } from '@/core/db/dexie'
import { sessionStore } from '@/core/auth/SessionStore'
import type { CatServico, OutProducao } from '@/core/db/entities'
import type { ApontamentoDraft, ProducaoSessionState } from './producaoStore'
import { pushProducaoOutbox, type PushSummary } from '@/core/sync/producaoSync'
import { triggerForegroundSync } from '@/core/sync/foregroundSync'
import { requestBgSync } from '@/core/sync/registerBgSync'

/**
 * Persiste todos os itens de uma sessão no outbox e dispara push imediato.
 *
 * Substitui `ProducaoRepository.saveSession()` do Android. Diferenças do nativo:
 *  - Background Sync (Service Worker) ainda não está cabeado — Fase 6.
 *    Por enquanto fazemos foreground push e o usuário vê o resultado na Success.
 *  - `cadastrador_nome` vai como snapshot no outbox, mas o backend ignora e usa o JWT.
 */
export async function saveProducaoSession(
  session: ProducaoSessionState,
): Promise<{ savedCount: number; pushResult: PushSummary | null }> {
  if (session.projetoId == null) {
    throw new Error('Sessão de produção sem projeto')
  }
  if (session.itens.length === 0) {
    throw new Error('Sessão sem apontamentos')
  }

  const user = sessionStore.current()
  if (!user) throw new Error('Sem sessão de usuário')

  const capturedAt = new Date().toISOString()

  const entities: OutProducao[] = session.itens.map((d) =>
    draftToEntity(d, session, user.userNome, capturedAt),
  )

  // bulkPut (upsert) em vez de bulkAdd para suportar re-finalizar sessão recarregada:
  // se o usuário abriu uma sessão existente, adicionou itens, e clicou Finalizar de novo,
  // os itens antigos (mesmo id) são atualizados em vez de gerar DataError por dupe key.
  // Novos itens (id novo) entram normal. SYNCED → SYNCED preserva idempotência no backend.
  // Antes de salvar, marcamos como PENDING para reenfileirar quem foi editado.
  await db.out_producao.bulkPut(entities)

  // Registra fila do SW para retry com backoff exponencial em Chrome/Android.
  // iOS Safari não suporta — foreground sync cobre.
  void requestBgSync('outbox-data')

  let pushResult: PushSummary | null = null
  try {
    pushResult = await pushProducaoOutbox()
  } catch (e) {
    // Push falhou — não é fatal, fica em out_producao para o sync retentar
    console.warn('[producaoRepo] push falhou; mantendo no outbox:', e)
  }

  // Dispara ciclo completo do engine em paralelo (sem await) — aciona feedback
  // visual do SyncStatusIndicator e também flusha fotos/carreteiro caso o
  // operador tenha pendência cruzada. ignoreCooldown porque post-save é UX
  // crítica: usuário acabou de salvar e quer ver a barrinha azul mexendo.
  void triggerForegroundSync('post-save-producao', { ignoreCooldown: true })

  return { savedCount: entities.length, pushResult }
}

/**
 * Carrega todos os apontamentos de uma sessão (mesmo projeto+dt+enc+equipe) e converte
 * de volta para ApontamentoDraft, permitindo o usuário continuar editando.
 *
 * Os ids dos drafts são preservados — quando re-finalizar (bulkPut), atualizam in-place.
 */
export async function loadSessionItensAsDrafts(args: {
  projetoId: number
  dt: string
  encarregadoId: number | null
  equipeId: number | null
}): Promise<ApontamentoDraft[]> {
  const rows = await db.out_producao
    .where('[projetoId+dt]')
    .equals([args.projetoId, args.dt])
    .toArray()

  const filtered = rows.filter(
    (r) =>
      (r.encarregadoId ?? null) === (args.encarregadoId ?? null) &&
      (r.equipeId ?? null) === (args.equipeId ?? null),
  )

  // Para reconstruir o draft precisamos do CatServico — busca em batch.
  const servicoIds = Array.from(new Set(filtered.map((r) => r.servicoExecutadoId)))
  const servicos = await db.cat_servico.bulkGet(servicoIds)
  const servicoMap = new Map<number, CatServico>()
  for (const s of servicos) if (s) servicoMap.set(s.id, s)

  return filtered.map((r) => outProducaoToDraft(r, servicoMap.get(r.servicoExecutadoId)))
}

function outProducaoToDraft(r: OutProducao, servico: CatServico | undefined): ApontamentoDraft {
  // Fallback: se serviço sumiu do catálogo (ex: SIGA inativou), reconstrói minimal
  // a partir dos snapshots em out_producao. UI mostra o nome certo mesmo assim.
  const fallback: CatServico = {
    id: r.servicoExecutadoId,
    nome: r.servicoExecutadoNome,
    tp: 'C',
    formulario: null,
    unidadeId: r.servicoExecutadoUnidadeId,
    unidadeNome: r.servicoExecutadoUnidadeNome,
    unidadeSigla: null,
    materialIdDefault: null,
    materialId: r.materialId,
    materialNome: r.materialNome,
    materialUnidadeId: r.materialUnidadeId,
    materialUnidadeNome: r.materialUnidadeNome,
    contratoItemId: null,
    coringa: false,
    bloqueioCubagem: false,
    isento: false,
    flagData: false,
    flagEquipe: false,
    flagEncarregado: false,
    flagQtdExecutada: true,
    flagQtdMaterial: false,
    flagAlturaMedia: false,
    flagMaterialRede: false,
    flagDn: false,
    flagTpRede: false,
    flagIdentificador: false,
    flagDtLancamento: false,
  }
  return {
    id: r.id,
    servico: servico ?? fallback,
    qtd: r.servicoExecutadoQtd,
    materialId: r.materialId,
    materialNome: r.materialNome,
    materialQtd: r.materialQtd,
    materialUnidadeId: r.materialUnidadeId,
    materialUnidadeNome: r.materialUnidadeNome,
    alturaMedia: r.alturaMedia,
    materialRedeId: r.materialRedeId,
    materialRedeNome: r.materialRedeNome,
    diametroId: r.diametroId,
    diametroNome: r.diametroNome,
    tpRedeId: r.tpRedeId,
    tpRedeNome: r.tpRedeNome,
    trechoId: r.trechoId,
    trechoNome: r.trechoNome,
    estaca: r.estaca,
    estacaInicial: r.estacaInicial,
    estacaFinal: r.estacaFinal,
    obs: r.obs,
  }
}

function draftToEntity(
  d: ApontamentoDraft,
  s: ProducaoSessionState,
  cadastradorNome: string,
  capturedAt: string,
): OutProducao {
  if (d.servico.unidadeId == null || d.servico.unidadeNome == null) {
    // Validação básica — backend exige unidade no DTO (positive int)
    throw new Error(`Serviço ${d.servico.id} (${d.servico.nome}) sem unidade no catálogo`)
  }
  return {
    id: d.id,
    projetoId: s.projetoId!,
    projetoCodigo: s.projetoCodigo,
    servicoExecutadoId: d.servico.id,
    servicoExecutadoNome: d.servico.nome,
    servicoExecutadoQtd: d.qtd,
    servicoExecutadoPrioridade: 'N',
    servicoExecutadoUnidadeId: d.servico.unidadeId,
    servicoExecutadoUnidadeNome: d.servico.unidadeNome,
    dt: s.dt,
    equipeId: s.equipeId,
    equipeNome: s.equipeNome,
    encarregadoId: s.encarregadoId,
    encarregadoNome: s.encarregadoNome,
    materialId: d.materialId,
    materialNome: d.materialNome,
    materialQtd: d.materialQtd,
    materialUnidadeId: d.materialUnidadeId,
    materialUnidadeNome: d.materialUnidadeNome,
    alturaMedia: d.alturaMedia,
    materialRedeId: d.materialRedeId,
    materialRedeNome: d.materialRedeNome,
    diametroId: d.diametroId,
    diametroNome: d.diametroNome,
    tpRedeId: d.tpRedeId,
    tpRedeNome: d.tpRedeNome,
    trechoId: d.trechoId,
    trechoNome: d.trechoNome,
    estaca: d.estaca,
    estacaInicial: d.estacaInicial,
    estacaFinal: d.estacaFinal,
    chuva: s.chuva ? 'S' : 'N',
    obs: d.obs,
    capturedAt,
    cadastradorNome,
    deletado: 'N',
    syncStatus: 'PENDING',
  }
}
